import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import api from '../services/api';
import { NOTIFICATIONS_CHANGED } from '../utils/notificationsBus';

/* Header bell — polls /api/notifications/unread-count on mount, on tab
   visibility, and every 60s while the tab is foregrounded. Tapping
   navigates to /notifications. The badge hides at 0 so the header
   stays quiet for users with nothing waiting.

   It ALSO refreshes the instant the inbox reads/dismisses/clears
   anything (NOTIFICATIONS_CHANGED) — without that, the badge stayed
   lit for up to a minute after the user had already read everything. */
export default function NotificationsBell({ className = '' }) {
  const [count, setCount] = useState(0);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/api/notifications/unread-count');
      setCount(res.data?.count || 0);
    } catch { /* silent — bell just stays at previous count */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = () => { if (!cancelled) refresh(); };
    tick();
    const interval = setInterval(tick, 60_000);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(NOTIFICATIONS_CHANGED, tick);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(NOTIFICATIONS_CHANGED, tick);
    };
  }, [refresh]);

  return (
    <button
      type="button"
      onClick={() => navigate('/notifications')}
      className={`relative p-2 rounded-btn hover:bg-paper-deep transition text-ink-2 ${className}`}
      aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ''}`}
    >
      <Bell className="w-5 h-5" />
      {count > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center"
          aria-hidden="true"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}
